import React from 'react';
import cx from 'classnames';
import ImageSelection from '../Global/ImageSelection/ImageSelection';
import RepeatAudioIcon from '../Global/Icons/RepeatAudioIcon';
import { optionKind, optionKinds } from './useOptionMediaPlayback';

// ImageSelection rewrites every option URL this way before rendering it. The media
// layout renders its own img, so it has to do the same or it bypasses the CDN.
const assetUrl = (imageUrl) =>
  String(imageUrl || '').replace('s3.amazonaws.com/amira-assets', process.env.ASSET_ROOT);

export default function MmcOptionsSection({
  hasOptions,
  hideOptions,
  isImageBased,
  isMediaBased,
  isVerticalText,
  effectiveOptions,
  animatedOptions,
  selectedKeys,
  highlightedOption,
  playingOption,
  canSelectOption,
  isRepeating,
  multiSelect,
  onOptionSelect,
  onImageLoadError,
  onAllImagesFailed,
  registerVideoElement,
  useGridLayout,
  narrationPassActive,
}) {
  if (!hasOptions) {
    return null;
  }

  const canClick = canSelectOption && !isRepeating;
  const isSelected = (key) => selectedKeys.includes(key);

  const tileClasses = (option, base) =>
    cx(base, {
      willAnimate: true,
      animate: animatedOptions.includes(option.key),
      selected: isSelected(option.key),
      highlighted: highlightedOption === option.key,
      playing: playingOption === option.key,
      // Only during the narration pass, so the spotlight never competes with a
      // selection the student made themselves.
      resting: narrationPassActive && playingOption !== option.key,
      useHover: canClick,
    });

  const gridClass = cx({ optionGrid: useGridLayout, narrating: narrationPassActive });

  const hasVideoOptions = effectiveOptions.some(
    (option) => optionKind(option) === optionKinds.video
  );

  return (
    <div
      className={cx('imageSelectionWrapper', {
        hidden: hideOptions,
        multiSelect,
      })}
    >
      {isMediaBased ? (
        <div className={cx('imageSelection mediaSelection', gridClass, { videoOptions: hasVideoOptions })}>
          {effectiveOptions.map((option) => {
            const formattedImageUrl = assetUrl(option.imageUrl);
            return (
            <div
              className={tileClasses(option, `imageBox mediaOptionBox mediaOptionBox--${optionKind(option)}`)}
              key={option.key}
              onClick={() => canClick && onOptionSelect(option.key)}
            >
              {optionKind(option) === optionKinds.video ? (
                <video
                  className="mediaOption__video"
                  ref={(el) => registerVideoElement(option.key, el)}
                  src={option.videoUrl}
                  playsInline
                  preload="auto"
                />
              ) : optionKind(option) === optionKinds.image ? (
                <img
                  className="mediaOption__image"
                  src={formattedImageUrl}
                  alt={option.text || ''}
                  onError={() => onImageLoadError?.(option.imageUrl, formattedImageUrl)}
                />
              ) : optionKind(option) === optionKinds.audio ? (
                // Spoken option with nothing to show. Placeholder icon pending the real asset.
                <div className="mediaOption__audio" aria-hidden="true">
                  <RepeatAudioIcon className="mediaOption__audioIcon" />
                </div>
              ) : null}
              {option.text && (
                <div className="answerBox textAnswerBox">
                  <span className="textAnswerLabel">{option.text}</span>
                </div>
              )}
            </div>
            );
          })}
        </div>
      ) : isImageBased ? (
        <ImageSelection
          options={effectiveOptions}
          handleSelection={(key) => canClick && onOptionSelect(key)}
          useAnimation={true}
          animatedOptions={animatedOptions}
          selectedOptions={selectedKeys}
          playingOption={playingOption}
          restOtherOptions={narrationPassActive}
          useGridLayout={useGridLayout}
          highlightedOption={highlightedOption}
          useHover={canClick}
          onImageLoadError={onImageLoadError}
          onAllImagesFailed={onAllImagesFailed}
        />
      ) : isVerticalText ? (
        <div className={cx('wordSelection', gridClass)}>
          {effectiveOptions.map((option) => (
            <div
              className={tileClasses(option, 'wordTile')}
              key={option.key}
              onClick={() => canClick && onOptionSelect(option.key)}
            >
              <span className="wordTile__label">{option.text}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className={cx('imageSelection textOptions', gridClass)}>
          {effectiveOptions.map((option) => (
            <div
              className={tileClasses(option, 'imageBox')}
              key={option.key}
              onClick={() => canClick && onOptionSelect(option.key)}
            >
              <div className="answerBox textAnswerBox">
                <span className="textAnswerLabel">{option.text || option.key}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
